<div [formGroup]="candidateForm">
	<div class="grid grid-cols-2 gap-4">
		<!-- Staff Number -->
		<div class="form-element">
			<mat-label for="form-name">Staff Number</mat-label>

			<mat-form-field appearance="outline" class="form-field-responsive mt-1 w-full">
				<input
					type="text"
					formControlName="staffNumber"
					matInput
					placeholder=""
					maxlength="7"
					[readonly]="!isOnline"
					(focusout)="onFocusOut($event)"
				/>
				@if (staffNumber.errors && !staffNumber.hasError("ownStaffNumber")) {
					<mat-error>
						{{ sharedService.getErrorMessage(staffNumber) }}
					</mat-error>
				}
			</mat-form-field>
			@if (staffNumber.hasError("ownStaffNumber")) {
				<p class="mt-1 text-sm text-red">{{ sharedService.errorMessages.ownStaffNumber }}</p>
			}
		</div>

		<!-- Candidate Name -->
		<div class="form-element">
			<mat-label for="form-name">Name</mat-label>
			<mat-form-field appearance="outline" class="form-field-responsive mt-1 w-full">
				<input type="text" formControlName="name" matInput placeholder="" [readonly]="isOnline" />
				@if (name.errors) {
					<mat-error>
						{{ sharedService.getErrorMessage(name) }}
					</mat-error>
				}
			</mat-form-field>
		</div>

		<!-- Candidate Rank -->
		<app-cbta-autocomplete
			[label]="'Rank'"
			[placeholder]="'Please Select'"
			[data]="rank.value"
			(focusOut)="saveRank($event)"
			[options]="filteredRankOptions"
			[field]="rank"
			[readonly]="false"
		></app-cbta-autocomplete>

		<!-- License Expiry Date -->
		@if (!excludeLicense) {
			<app-cbta-datepicker
				[datePickerInput]="licenseExpiryDate"
				[inputDesign]="'formType'"
				label="License Expiry Date"
				placeHolder="DD/MM/YY"
				[defaultValue]="licenseExpiryDate.value"
				[minDate]="today"
				[format]="DATE_FORMAT.LICENSE_DATE_FORMAT"
				(selectedDateChange)="handleDateChange($event)"
				(onDatePickerInit)="onDatePickerInit($event)"
				[isDisabled]="licenseExpiryDate.disabled"
			></app-cbta-datepicker>

			<!-- License Number -->
			<div class="form-element">
				<mat-label for="form-name">License Number</mat-label>
				<mat-form-field appearance="outline" class="form-field-responsive mt-1 w-full">
					<input type="text" formControlName="licenseNo" matInput placeholder="" />
					@if (licenseNo.errors) {
						<mat-error>
							{{ sharedService.getErrorMessage(licenseNo) }}
						</mat-error>
					}
				</mat-form-field>
			</div>

			<!-- License Type -->
			<app-cbta-autocomplete
				[label]="'License Type'"
				[placeholder]="'Please Select'"
				[data]="licenseType.value"
				(focusOut)="saveLicenseType($event)"
				[options]="sharedService.getLicenseTypeOptions(formType)"
				[field]="licenseType"
				[readonly]="false"
			></app-cbta-autocomplete>
		}
	</div>

	<!-- Session Configuration (style parity with Create Form) -->
	<div formGroupName="sessionDetails" class="py-6" id="session-checkboxes">
		<div class="mb-4 text-lg font-medium text-black">
			Session Details: <span class="underline">Candidate {{ parentFormArrayIndex + 1 }}</span>
		</div>
		<div class="mb-6 text-grey-60">
			Please select relevant session. Instructor can select more than <strong>ONE(1)</strong>.
		</div>

		<!-- 1 group → spread into 4 columns -->
		@if (sessionGroups.length === 1) {
			<div
				[formGroupName]="sessionGroups[0].code"
				class="grid grid-cols-1 gap-x-16 gap-y-3 sm:grid-cols-3 lg:grid-cols-4"
			>
				@for (col of singleCols; track $index) {
					<div class="flex flex-col gap-3">
						@for (s of col; track s.code) {
							<mat-checkbox [formControlName]="s.code" color="primary">
								{{ s.description }}
							</mat-checkbox>
						}
					</div>
				}
			</div>
		}

		<!-- 2 groups (compact) → right has <= 4 items -->
		@if (sessionGroups.length === 2 && twoGroupsCompact) {
			<div class="grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-2">
				<!-- Left -->
				<!-- FIX: use non-null assertion since we are inside a guard that implies existence -->
				<div [formGroupName]="twoColsLeft!.code">
					<div class="mb-2 font-semibold">{{ twoColsLeft!.key }}</div>
					<div class="flex flex-col gap-3">
						@for (s of twoColsLeft!.items; track s.code) {
							<mat-checkbox [formControlName]="s.code" color="primary">
								{{ s.description }}
							</mat-checkbox>
						}
					</div>
				</div>

				<!-- Right (single column, <= 4 items) -->
				<div [formGroupName]="sessionGroups[1].code">
					<div class="mb-2 font-semibold">{{ secondGroupTitle }}</div>
					<div class="flex flex-col gap-3">
						@for (s of sessionGroups[1].items; track s.code) {
							<mat-checkbox [formControlName]="s.code" color="primary">
								{{ s.description }}
							</mat-checkbox>
						}
					</div>
				</div>
			</div>
		}

		<!-- 2 groups (non-compact) → left = group A, right = group B split into 2 columns -->
		@if (sessionGroups.length === 2 && !twoGroupsCompact) {
			<div class="grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-3">
				<!-- FIX: non-null assertion -->
				<div [formGroupName]="twoColsLeft!.code">
					<div class="mb-2 font-semibold">{{ twoColsLeft!.key }}</div>
					<div class="flex flex-col gap-3">
						@for (s of twoColsLeft!.items; track s.code) {
							<mat-checkbox [formControlName]="s.code" color="primary">
								{{ s.description }}
							</mat-checkbox>
						}
					</div>
				</div>

				@for (col of twoColsRight; track $index) {
					<div [formGroupName]="sessionGroups[1].code">
						@if ($index === 0) {
							<div class="mb-2 font-semibold">{{ secondGroupTitle }}</div>
						} @else {
							<div class="mb-2">&nbsp;</div>
						}
						<div class="flex flex-col gap-3">
							@for (s of col; track s.code) {
								<mat-checkbox [formControlName]="s.code" color="primary">
									{{ s.description }}
								</mat-checkbox>
							}
						</div>
					</div>
				}
			</div>
		}

		<!-- 3 or more groups → each is its own column -->
		@if (sessionGroups.length >= 3) {
			<div class="grid grid-cols-1 gap-x-12 gap-y-6 sm:grid-cols-3 lg:grid-cols-3">
				@for (g of sessionGroups; track g.code) {
					<div [formGroupName]="g.code">
						<div class="mb-2 font-semibold">{{ g.key }}</div>
						<div class="flex flex-col gap-3">
							@for (s of g.items; track s.code) {
								<mat-checkbox [formControlName]="s.code" color="primary">
									{{ s.description }}
								</mat-checkbox>
							}
						</div>
					</div>
				}
			</div>
		}

		@if (sessionGroups.length === 0) {
			<p class="italic text-gray-500">No sessions available.</p>
		}
	</div>
</div>
